<template>
    <SbsBaseButton
        @click.capture="preventClickIfDisabled"
        v-bind="$attrs"
        class="sbs-link group trs"
        :class="linkClasses"
        :to="to"
        :disabled="disabled"
        :tabindex="tabindex"
        tag="a">
        <!-- @slot Слот перед контентом -->
        <slot name="prepend"></slot>

        <span class="sbs-link__text-wrapper">
            <!-- @slot Слот по-умолчанию -->
            <slot>
                <span class="sbs-link__text">
                    {{ text }}
                </span>
            </slot>

            <!-- @slot Слот для нижнего подчеркивания -->
            <slot
                v-if="underlined"
                name="underline">
                <span
                    class="sbs-link__underline trs"
                    :class="linkUnderlineClasses"></span>
            </slot>
        </span>

        <!-- @slot Слот после контента -->
        <slot name="append"></slot>
    </SbsBaseButton>
</template>

<script>
import { linkSizes, LINK_SIZES } from '../../utils/sizes.js';
import themesMixin from '../../mixins/themes.js';
import { AllColors } from '../../utils/colors.js';

/**
 * Компонент ссылки
 */
export default {
    name: 'SbsLink',
    mixins: [themesMixin],
    computed: {
        tabindex() {
            return this.disabled ? '-1' : null;
        },
        listeners() {
            return {
                ...this.$attrs,
                click: this.linkClickHandler,
            };
        },
        linkClasses() {
            const classes = [...this.themeClasses, this.sizeClass];

            return classes;
        },
        linkUnderlineClasses() {
            const classes = [];

            if (this.underlined) {
                if (this.underlineTheme) {
                    classes.push(`bg-${this.underlineTheme}`);
                }

                if (this.hoverUnderline && !this.disabled) {
                    classes.push(`group-hover-bg-${this.hoverUnderline}`);
                }
            }

            return classes;
        },
        sizeClass() {
            return `sbs-link--${this.size}`;
        },
    },
    methods: {
        linkClickHandler(e) {
            e.currentTarget.blur();
            /**
             * Событие `click` срабатывает при клике.
             * В качестве параметра объект события
             */
            this.$emit('click', e);
        },
        /**
         * @param {MouseEvent} e
         * @returns {boolean}
         */
        preventClickIfDisabled(e) {
            if (this.disabled) {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation(); // for firefox
                // eslint-disable-next-line
                return false;
            }
        },
    },
    inheritAttrs: false,
    props: {
        /**
         * Тема (цветовые решения) для цвета текста
         */
        // eslint-disable-next-line vue/no-unused-properties
        colorTheme: {
            type: String,
            default: AllColors.SUCCESS,
        },
        /**
         * Отключает взаимодействие с компонентом
         */
        disabled: {
            type: Boolean,
            default: false,
        },
        /**
         * Тема (цветовые решения) для цвета нижнего подчеркивания при наведении
         */
        hoverUnderline: {
            type: String,
            default: '',
        },
        /**
         * Текст кнопки
         */
        text: {
            type: String,
            default: '',
        },
        /**
         * Роут для `<RouterLink>` для перехода на другую страницу без перезагрузки.
         * Может быть строкой, либо объектом.
         * Имеет более высокий приоритет перед `href`
         */
        to: {
            type: [Object, String],
            default: null,
        },
        /**
         * Определяет наличие нижнего подчеркивания
         */
        underlined: {
            type: Boolean,
            default: true,
        },
        /**
         * Тема (цветовые решения) для нижнего подчеркивания
         */
        underlineTheme: {
            type: String,
            default: AllColors.GRAY_LIGHT,
        },
        /**
         * Размер текста ссылки (Large, Compact, Regular)
         */
        size: {
            type: String,
            default: linkSizes.REGULAR,
            valdator: value => LINK_SIZES.includes(value),
        },
    },
};
</script>

<style lang="scss">
.sbs-link {
    position: relative;
    display: inline-flex;
    align-items: center;
    text-decoration: none;
    cursor: pointer;

    &--large {
        @include typo-primary-book;
    }

    &--regular {
        @include typo-secondary-book;
    }

    &--small {
        @include typo-subsecondary-book;
    }

    &.disabled {
        color: $gray;
    }

    & > * {
        color: inherit;
    }

    &__text-wrapper {
        display: flex;
        flex-direction: column;
        justify-content: center;

        &::after {
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            z-index: 1;
            content: "";
        }
    }

    &__underline {
        height: 1px;
    }
}
</style>
